import useAlias from '@alias';
import { useMediaQuery, useTheme } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import useDeviceTableState from '@src/hooks/devices/useDeviceTableState';
import useColumnWidths from '@src/hooks/useColumnWidths';
import { memo, ReactElement, useCallback, useEffect, useMemo } from 'react';
import DualViewDataGrid from './DualViewDataGrid';
import useDeviceTableColumns from './hooks/useDeviceTableColumns';
import useDeviceTableRows from './hooks/useDeviceTableRows';
import SingleViewDataGrid from './SingleViewDataGrid';
import { DeviceDataTableProps } from './types';

const DeviceDataTable: React.FC<DeviceDataTableProps> = ({
  checkboxSelection,
  disableMultipleRowSelection = false,
  onRowSelectionChange,
  rowSelectionModel,
  unitSelectionModel,
  dataPoints,
  pointConfigRefs,
  enableCmdBtnColumn,
  disableCmdBtn,
  disableCmdBtnByInterlock,
  enableNotesColumn,
  openCmdModal,
  devices = [],
  allowExpandedCount,
  initialExpandedDetailPanelIds,
  boxStyle,
  additionalClassnames,
  useDualListView = false,
  customColumn,
  onCollapseAllReady,
  onExpandedRowIdsChange,
  widgetId,
  ...restProps
}): ReactElement => {
  const alias = useAlias();

  const { expandedRowIds, sortModel, onExpandedRowsChange, onSortModelChange } =
    useDeviceTableState({
      initialExpandedRowIds: initialExpandedDetailPanelIds,
      allowExpandedCount,
    });

  const collapseAll = useCallback(() => {
    onExpandedRowsChange([]);
  }, [onExpandedRowsChange]);

  // Notify parent when expanded rows change
  useEffect(() => {
    onExpandedRowIdsChange?.(expandedRowIds);
  }, [expandedRowIds, onExpandedRowIdsChange]);

  useEffect(() => {
    onCollapseAllReady?.(collapseAll);
  }, [onCollapseAllReady, collapseAll]);

  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));

  const { initialColumnWidths, onColumnWidthChange } =
    useColumnWidths(widgetId);

  const baseColumns = useDeviceTableColumns({
    pointConfigRefs,
    enableCmdBtnColumn,
    enableNotesColumn,
    customColumn,
    disableButton: disableCmdBtn,
    disabledByInterlock: disableCmdBtnByInterlock,
    openCmdModal,
    alias,
  });

  // Apply persisted column widths (initialColumnWidths is stable, won't trigger re-renders)
  // When a persisted width exists, remove flex so width takes precedence
  const columns = useMemo(
    () =>
      baseColumns.map((col) => {
        const persistedWidth = initialColumnWidths[col.field];
        if (persistedWidth !== undefined) {
          const { flex: _flex, ...rest } = col;
          return { ...rest, width: persistedWidth };
        }
        return col;
      }),
    [baseColumns, initialColumnWidths],
  );

  const rows = useDeviceTableRows({ dataPoints, devices });

  // Split rows for dual view
  const { rowsFirstGrid, rowsSecondGrid } = useMemo(() => {
    if (!useDualListView || !isLargeScreen) {
      return { rowsFirstGrid: rows, rowsSecondGrid: [] };
    }
    const midpoint = Math.ceil(rows.length / 2);
    return {
      rowsFirstGrid: rows.slice(0, midpoint),
      rowsSecondGrid: rows.slice(midpoint),
    };
  }, [rows, useDualListView, isLargeScreen]);

  const shouldShowDualView = useDualListView && isLargeScreen;

  const initialState = useMemo(
    () => ({
      columns: { columnVisibilityModel: { id: false } },
      detailPanel: { expandedRowIds: initialExpandedDetailPanelIds ?? [] },
    }),
    [initialExpandedDetailPanelIds],
  );

  const handleRowSelectionChange = useCallback(
    (rowSelectionModel: GridRowSelectionModel): void => {
      onRowSelectionChange?.(rowSelectionModel);
    },
    [onRowSelectionChange],
  );

  const boxStyles = useMemo(
    () => ({
      height: 'calc(100% - 47px)',
      ...boxStyle,
    }),
    [boxStyle],
  );
  const className = useMemo(
    () => `MuiDataGrid-DeviceDataTable ${additionalClassnames ?? ''}`.trim(),
    [additionalClassnames],
  );

  const gridProps = {
    rowSelectionModel: rowSelectionModel ?? unitSelectionModel,
    throttleRowsMs: 1000,
    onRowSelectionModelChange: handleRowSelectionChange,
    checkboxSelection: checkboxSelection,
    sortModel,
    onSortModelChange,
    ...restProps,
    initialState,
    className: className,
    disableMultipleRowSelection,
    columns,
    detailPanelExpandedRowIds: expandedRowIds,
    onDetailPanelExpandedRowIdsChange: onExpandedRowsChange,
    onColumnWidthChange,
  };

  if (shouldShowDualView) {
    return (
      <DualViewDataGrid
        rowsFirstGrid={rowsFirstGrid}
        rowsSecondGrid={rowsSecondGrid}
        boxStyles={boxStyles}
        gridProps={gridProps}
      />
    );
  }

  return (
    <SingleViewDataGrid
      rows={rows}
      boxStyles={boxStyles}
      gridProps={gridProps}
    />
  );
};

DeviceDataTable.displayName = 'DeviceDataTable';

export default memo(
  DeviceDataTable,
  (prevProps, nextProps) =>
    prevProps.dataPoints === nextProps.dataPoints &&
    prevProps.pointConfigRefs === nextProps.pointConfigRefs &&
    prevProps.devices === nextProps.devices &&
    prevProps.checkboxSelection === nextProps.checkboxSelection &&
    prevProps.disableMultipleRowSelection ===
      nextProps.disableMultipleRowSelection &&
    prevProps.enableCmdBtnColumn === nextProps.enableCmdBtnColumn &&
    prevProps.disableCmdBtn === nextProps.disableCmdBtn &&
    prevProps.disableCmdBtnByInterlock === nextProps.disableCmdBtnByInterlock &&
    prevProps.enableNotesColumn === nextProps.enableNotesColumn &&
    prevProps.openCmdModal === nextProps.openCmdModal &&
    prevProps.unitSelectionModel === nextProps.unitSelectionModel &&
    prevProps.rowSelectionModel === nextProps.rowSelectionModel &&
    prevProps.allowExpandedCount === nextProps.allowExpandedCount &&
    prevProps.initialExpandedDetailPanelIds ===
      nextProps.initialExpandedDetailPanelIds &&
    prevProps.onRowSelectionChange === nextProps.onRowSelectionChange &&
    prevProps.boxStyle === nextProps.boxStyle &&
    prevProps.additionalClassnames === nextProps.additionalClassnames &&
    prevProps.useDualListView === nextProps.useDualListView &&
    prevProps.onCollapseAllReady === nextProps.onCollapseAllReady &&
    prevProps.customColumn === nextProps.customColumn &&
    prevProps.onExpandedRowIdsChange === nextProps.onExpandedRowIdsChange &&
    prevProps.widgetId === nextProps.widgetId,
);

export { getRows } from './rowUtils';
export type { DeviceDataTableProps, DeviceDataTableRow } from './types';
